import { ParamValues } from '../../components/Crud/useFilter.types';
import { BaseItem } from '../../components/ListV2/components/table/VirtualizedTable.types';
import { SelectedFilter } from '../../lib/FilterLib';
import { Option } from '../../lib/OptionsLib';
import { Storefront } from '../../services/modules/storefront/storefront.types';
import { Brand } from '../brand-admin/brand.types';
import { Classification } from '../classifications/classification.types';
import { Collection } from '../collections/collections.types';
import { Customer } from '../customer/types/customer.types';
import { CustomerStatusType } from '../customer/types/customerStatusType';
import { OrderStatus } from '../orders/interfaces/order-status.interface';
import { StatusType } from '../orders/order.types';
import { CompactAvailability, DeliveryWindow } from '../products/ProductTypes';
import { OpportunityTypeEnum } from '../sales-target/sales-target.types';
import { User } from '../user-admin/user.types';
import { SalesOpportunityCreatedBy } from './enums/createdBy.enum';
import { SalesOpportunityCategory } from './salesOpportunityCategory.types';

export enum PhaseType {
  CLOSED_AND_WINS = 'CLOSED_AND_WINS',
  SCHEDULED = 'SCHEDULED',
  CLOSED_AND_LOST = 'CLOSED_AND_LOST',
  OPEN = 'OPEN',
}

export enum LostReasonType {
  UNATTRACTIVE_COLLECTION = 'UNATTRACTIVE_COLLECTION',
  UNATTRACTIVE_BUSINESS_POLICY = 'UNATTRACTIVE_BUSINESS_POLICY',
  HIGH_STOCK = 'HIGH_STOCK',
  BLOCKED_CLIENT = 'BLOCKED_CLIENT',
  UNATTRACTIVE_PRICE = 'UNATTRACTIVE_PRICE',
  BILLING_ISSUES = 'BILLING_ISSUES',
  REPLACEMENT_BY_FRANCHISE_OR_STORE = 'REPLACEMENT_BY_FRANCHISE_OR_STORE',
  REPLACEMENT_BY_ANOTHER_RETAILER = 'REPLACEMENT_BY_ANOTHER_RETAILER',
  WEBSITE_CONFLICT = 'WEBSITE_CONFLICT',
  WILL_BUY_NEXT_COLLECTION = 'WILL_BUY_NEXT_COLLECTION',
  CUSTOMER_FROM_ANOTHER_BRAND_OF_GROUP = 'CUSTOMER_FROM_ANOTHER_BRAND_OF_GROUP',
  CNPJ_CHANGE = 'CNPJ_CHANGE',
  STORE_CLOSED = 'CLOSED_THE_STORE',
  NATURAL_DISASTERS = 'NATURAL_DISASTERS',
}

export enum SalesOpportunityDelayedStatus {
  DELAYED = 'DELAYED',
  NOT_DELAYED = 'NOT_DELAYED',
}

export type SalesOpportunityDetailedAchievedValue = {
  [StatusType.APPROVED]?: number;
  [StatusType.DRAFT]?: number;
  [StatusType.SENT]?: number;
  [StatusType.IN_REVISION]?: number;
  [StatusType.ON_APPROVAL]?: number;
};

export type SalesOpportunity = BaseItem & {
  id: string;
  code: string;
  availability: CompactAvailability[];
  deliveryWindows?: DeliveryWindow[];
  lostReason: LostReasonType;
  collections?: Collection[];
  phase: PhaseType;
  periodType: OpportunityTypeEnum;
  value: number;
  startDate?: string | null;
  endDate?: string | null;
  scheduledDate?: Date | null;
  customer: Customer;
  classifications?: Classification[];
  deliveryWindowsNames?: string[];
  classificationsNames?: string[];
  salesOpportunitiesCategories: SalesOpportunityCategory[];
  salesOpportunitiesHistoric: SalesOpportunityHistoric[];
  observation?: string;
  orderCount: number;
  achievedValue: number;
  opportunityValue: number;
  conversion: number;
  detailedAchievedValue: SalesOpportunityDetailedAchievedValue;
};

export type SalesOpportunityHistoric = {
  id: string;
  createdAt: string;
  salesOpportunity: SalesOpportunity;
  user?: User;
  store?: Storefront;
  brand: Brand;
  value: number;
  phase: PhaseType;
  createdBy: SalesOpportunityCreatedBy;
};

export type SalesOpportunityTotals = {
  value: number;
  averageValue: number;
  orderCount: number;
  achievedValue: number;
  opportunityValue: number;
  conversion: number;
  averageOrders: number;
  averageAchievedValue: number;
};

export type Conversion = {
  opportunityValue: number;
  orderCount: number;
  achievedValue: number;
  conversion: number;
  percentValue: number;
};

export type SalesOpportunityConversion = {
  salesOpportunityId: string;
} & Conversion;

export type SalesOpportunityConversionByCategory = {
  categoryId: string;
  categoryName: string;
} & Conversion;

export type SalesOpportunityConversionTotals = {
  opportunitiesTotalValue: number;
  opportunitiesAverageValue: number;
  ordersTotalCount: number;
  averageOrders: number;
  achievedTotalValue: number;
  achievedAverageValue: number;
  conversion: number;
};

export type OpportunityFilter = {
  code?: string;
  customerCodeOrNameOrCpfcnpj?: string;
  productCollectionsIds?: string | string[];
  orderCollectionsIds?: string | string[];
  createdAtStartDate?: string;
  createdAtEndDate?: string;
  availability?: CompactAvailability | CompactAvailability[];
  deliveryWindowsIds?: string | string[];
  phase?: PhaseType[];
  customerSalesRepresentativeNameOrCode?: string;
  customerCity?: string;
  customerState?: string;
  customerStatus?: CustomerStatusType | CustomerStatusType[];
  orderStatus?: StatusType | StatusType[];
  classificationIds?: string;
};

export type OpportunityFilterOptions = {
  collections: Option[];
  deliveryWindows: Option[];
  classifications: Option[];
};

export type OpportunityFilterProps = {
  options: OpportunityFilterOptions;
  filtersFromUrl: ParamValues;
  selectedFilters: SelectedFilter[];
};

export type BrandSalesOpportunityConfig = {
  id: string;
  delayedOpportunityConsideredStatus: OrderStatus[];
  delayedOpportunityConsideredConversion: number;
  delayedOpportunityConsideredDays: number;
  autoCloseOpportunityConsideredStatus: OrderStatus[];
  autoCloseOpportunityConversion: number;
  autoCloseOpportunity: boolean;
  useDelayedOpportunity: boolean;
};

export type SalesOpportunityChangePhaseUpdate = {
  phase: PhaseType;
  lostReason?: LostReasonType;
  salesOpportunityIds: string[];
  scheduledDate?: Date;
  filter: ParamValues;
};
